/**
 * Versioned identifiers for the Gemini reviewer contract (D-093, D-094).
 *
 * These are shared by the client and the Worker deliberately. D-095 chose to
 * keep the Worker in this repository so the request contract, the response
 * contract, the documentation and the tests move in a single change — a second
 * repository would put these constants in two places with no test that can see
 * both, which is the drift the versioning exists to prevent.
 *
 * They are separate from `models/schemaVersion.ts` and move independently of it
 * (D-023). Nothing here touches the database or the export.
 */

/**
 * The request and response shape.
 *
 * The Worker rejects a request that does not carry this exact value, rather
 * than accepting a range. A client and a Worker that disagree about the shape
 * have no safe way to guess which side is older, and `unsupportedContractVersion`
 * exists to say so plainly.
 *
 * **Adding a field to the payload requires an explicit decision, a bump here,
 * updated preview copy and tests** (D-093 §2). It is not a change made in
 * passing, because every field added is a field disclosed.
 */
export const GEMINI_CONTRACT_VERSION = 1

/**
 * The system instruction that produced a proposal.
 *
 * **This is a contract artefact, not ordinary code** (D-094 §4). The prompt is
 * the single input that most shapes what the user is told, and it would
 * otherwise be the least controlled thing in the system: a quality regression
 * would have nothing to bisect against and "why was last month's advice
 * different?" would be unanswerable.
 *
 * So it is stored on every proposal, and **changing the instruction requires a
 * written decision** — the same discipline `EXPORT_FORMAT_VERSION` already
 * lives by. The accepted cost is that a typo fix in the prompt becomes a
 * versioned change.
 *
 * The instruction text itself lives in the Worker, because D-093 forbids the
 * client from selecting or influencing it. Only its identifier is shared.
 */
export const GEMINI_PROMPT_VERSION = 'sola-review-v1'

/**
 * The header the client presents its token in.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  SHARED BECAUSE THE TWO ENDS DISAGREED, IN SHIPPED CODE, FOR THREE PHASES
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Until C7.0's live check this name existed **twice**: `worker/src/auth.ts`
 *  read `X-Sola-Token`, and the client sent `Authorization: Bearer <token>`. So
 *  every real request from the app was refused, three ways over — the header
 *  names did not match, the client wrapped the value in a `Bearer ` prefix the
 *  Worker's literal comparison would never accept, and `Authorization` was not
 *  in the CORS allowlist, so a browser preflight would have blocked it before it
 *  was sent at all.
 *
 *  **Nothing caught it.** Every browser test intercepts `**\/api/review` and
 *  every unit test injects `fetchImpl`, so no test in the repository ever put
 *  the client's headers next to the Worker's expectations. It was found by one
 *  `curl` against the deployed development Worker, which is the same lesson
 *  phase 2 recorded about the output cap and phase 4 recorded about
 *  `contractVersion`: **a fixture that nothing compares to the real thing will
 *  drift, and the drift is invisible until something real reads it.**
 *
 *  It lives here so there is one name. **Do not re-declare it on either side.**
 *
 *  A header rather than a query parameter, because query parameters reach logs.
 *  A bare token rather than `Bearer`, because the Worker compares the pasted
 *  string literally (D-095 §1) and a prefix would make the stored credential and
 *  the transmitted one different things.
 */
export const SOLA_TOKEN_HEADER = 'X-Sola-Token'
